import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { InputFieldComponent } from '../../../../../shared/components/input-field/input-field';
import { CustomValidators } from '../../../../../shared/validators/validators';
import { NotificationService } from '../../../../../shared/services/notification.service';
import { LoginService } from '../../../services/login.service';
import { AUTH_MESSAGES } from '../../../../../shared/constants/message.constants';
import { ChangePasswordRequest, SendPasswordRequest } from '../../../../../shared/interfaces/login.interface';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-forgot-password-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, InputFieldComponent],
  templateUrl: './forgot-password-modal.component.html',
  styleUrl: './forgot-password-modal.component.css'
})
export class ForgotPasswordModalComponent implements OnInit, OnDestroy {
  changePasswordForm: FormGroup = new FormGroup({});
  currentPasswordError: string = '';
  newPasswordError: string = '';
  confirmPasswordError: string = '';
  currentPasswordHasError: boolean = false;
  newPasswordHasError: boolean = false;
  confirmPasswordHasError: boolean = false;
  isLoading: boolean = false;
  private subscriptions: Subscription[] = [];
  
  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private loginService: LoginService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  private initForm(): void {
    this.changePasswordForm = this.fb.group({
      currentPassword: ['', [CustomValidators.required]],
      newPassword: ['', [CustomValidators.required, CustomValidators.passwordStrength]],
      confirmPassword: ['', [CustomValidators.required]]
    });
  }
  
  onCurrentPasswordChange(value: string) {
    this.changePasswordForm.patchValue({ currentPassword: value });
    if (this.currentPasswordHasError && value.trim() !== '') {
      this.currentPasswordHasError = false;
      this.currentPasswordError = '';
    }
  }
  
  onNewPasswordChange(value: string) {
    this.changePasswordForm.patchValue({ newPassword: value });
    if (this.newPasswordHasError && value.trim() !== '') {
      this.newPasswordHasError = false;
      this.newPasswordError = '';
    }
    this.validateConfirmPassword();
  }
  
  onConfirmPasswordChange(value: string) {
    this.changePasswordForm.patchValue({ confirmPassword: value });
    if (this.confirmPasswordHasError && value.trim() !== '') {
      this.confirmPasswordHasError = false;
      this.confirmPasswordError = '';
    }
    this.validateConfirmPassword();
  }

  onCurrentPasswordErrorClear() {
    this.currentPasswordError = '';
    this.currentPasswordHasError = false;
  }

  onNewPasswordErrorClear() {
    this.newPasswordError = '';
    this.newPasswordHasError = false;
  }

  onConfirmPasswordErrorClear() {
    this.confirmPasswordError = '';
    this.confirmPasswordHasError = false;
  }

  private validateCurrentPassword(): void {
    const currentPasswordControl = this.changePasswordForm.get('currentPassword');
    if (currentPasswordControl?.errors) {
      this.currentPasswordHasError = true;
      if (currentPasswordControl.errors['required']) {
        this.currentPasswordError = 'Current password is required';
      }
    }
  }

  private validateNewPassword(): void {
    const newPasswordControl = this.changePasswordForm.get('newPassword');
    if (newPasswordControl?.errors) {
      this.newPasswordHasError = true;
      if (newPasswordControl.errors['required']) {
        this.newPasswordError = 'New password is required';
      } else if (newPasswordControl.errors['minLength']) {
        this.newPasswordError = 'Password must be at least 8 characters';
      } else if (newPasswordControl.errors['passwordStrength']) {
        this.newPasswordError = 'Password must contain uppercase, lowercase, and number';
      }
    }
  }

  private validateConfirmPassword(): void {
    const newPassword = this.changePasswordForm.get('newPassword')?.value;
    const confirmPassword = this.changePasswordForm.get('confirmPassword')?.value;
    
    if (confirmPassword && newPassword !== confirmPassword) {
      this.confirmPasswordHasError = true;
      this.confirmPasswordError = 'Passwords do not match';
    } else if (this.confirmPasswordHasError && newPassword === confirmPassword) {
      this.confirmPasswordHasError = false;
      this.confirmPasswordError = '';
    }
  }

  private clearErrors(): void {
    this.currentPasswordError = '';
    this.newPasswordError = '';
    this.confirmPasswordError = '';
    this.currentPasswordHasError = false;
    this.newPasswordHasError = false;
    this.confirmPasswordHasError = false;
  }
  
  onSubmit() {
    this.clearErrors();
    this.validateCurrentPassword();
    this.validateNewPassword();
    this.validateConfirmPassword();

    if (this.changePasswordForm.valid) {
      this.isLoading = true;
      // const changePasswordRequest: ChangePasswordRequest = {
      //   currentPassword: this.changePasswordForm.value.currentPassword,
      //   newPassword: this.changePasswordForm.value.newPassword,
      //   confirmPassword: this.changePasswordForm.value.confirmPassword
      // };

      // if(this.changePasswordForm.value.newPassword===this.changePasswordForm.value.confirmPassword)
      // {
        const sendPasswordRequest: SendPasswordRequest= {
          oldPassword: this.changePasswordForm.value.currentPassword,
          newPassword: this.changePasswordForm.value.newPassword,
        };
      // }

      this.subscriptions.push(
        this.loginService.changePassword(sendPasswordRequest).subscribe({
          next: (response) => {
            this.isLoading = false;
            
            if (response.isSuccess) {
              this.notificationService.showSuccess('Password changed successfully');
              this.router.navigate(['/login']);
            } else {
              this.notificationService.showFailure(response.message || 'Failed to change password');
            }
          },
          error: (error) => {
            this.isLoading = false;
            // this.notificationService.showFailure('An error occurred while changing password');
          }
        })
      );
    }
  }


  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  onCancel(): void {
    this.activeModal.dismiss();
  }

}
